import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import { BarcodeIcon } from '@/ui/icons/barcode'
import { DownloadIcon } from '@/ui/icons/download'
import { PrinterIcon } from '@/ui/icons/printer'
import { LabelSheet } from './label-sheet'

// Long enough to read as the label coming out of the printer, short enough that
// the browser still treats the download as part of the click.
const PRINT_ANIMATION_MS = 1100

interface LabelStatusProps {
  labelURL?: string | null
  isLoading: boolean
  isChecking: boolean
  onCheckAgain: () => void
  processId?: string
  expired?: boolean
  isRequestingNewLabel?: boolean
  newLabelFailed?: boolean
  onRequestNewLabel?: () => void
}

export const LabelStatus = ({
  labelURL,
  isLoading,
  isChecking,
  onCheckAgain,
  processId,
  expired = false,
  isRequestingNewLabel = false,
  newLabelFailed = false,
  onRequestNewLabel = () => undefined
}: LabelStatusProps) => {
  if (isLoading) {
    return <LabelSkeleton />
  }

  if (expired) {
    return (
      <LabelExpired
        isRequesting={isRequestingNewLabel}
        failed={newLabelFailed}
        onRequest={onRequestNewLabel}
      />
    )
  }

  if (labelURL) {
    return isImageLabelURL(labelURL) ? (
      <QrLabel labelURL={labelURL} processId={processId} />
    ) : (
      <LabelReady labelURL={labelURL} processId={processId} />
    )
  }

  return <LabelGenerating isChecking={isChecking} onCheckAgain={onCheckAgain} />
}

// Image labels are carrier QR codes scanned at the counter (e.g. DHL
// printless), so they render inline instead of behind a download link.
export const isImageLabelURL = (url: string | null | undefined): boolean => {
  if (!url) return false
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.png')
  } catch {
    return false
  }
}

const LabelSkeleton = () => {
  return (
    <div className="flex flex-col items-center gap-4 px-4 py-5" aria-hidden>
      <div className="h-[240px] w-[180px] animate-pulse rounded bg-neutral-100" />
      <div className="h-9 w-full animate-pulse rounded-md bg-neutral-100" />
    </div>
  )
}

const LabelGenerating = ({
  isChecking,
  onCheckAgain
}: {
  isChecking: boolean
  onCheckAgain: () => void
}) => {
  const { t } = useTranslation()

  return (
    <div
      role="status"
      className="flex items-center gap-4 p-4 lg:flex-col lg:py-5 lg:text-center"
    >
      <LabelPrinter feed="printed">
        <LabelSheet className="w-full" blank />
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-6 animate-print-head-loop border-b-2 border-blue-500/50 bg-gradient-to-b from-blue-500/20 to-transparent motion-reduce:hidden"
        />
        <div
          aria-hidden
          className="absolute -inset-x-full inset-y-0 animate-shimmer bg-gradient-to-r from-transparent via-blue-500/10 to-transparent motion-reduce:hidden"
        />
      </LabelPrinter>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-3 lg:items-center lg:gap-4">
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-neutral-900">
            {t('tracking_new.label_generating_title')}
          </p>
          <p className="text-xs font-normal leading-relaxed text-neutral-600">
            {t('tracking_new.label_generating_body')}
          </p>
        </div>
        <button
          type="button"
          onClick={onCheckAgain}
          disabled={isChecking}
          className="rounded-md border border-neutral-300/70 bg-white px-3 py-1.5 text-xs font-medium text-neutral-900 shadow-sm transition-colors hover:bg-neutral-100 disabled:opacity-60"
        >
          {t(
            isChecking
              ? 'tracking_new.label_checking'
              : 'tracking_new.label_check_again'
          )}
        </button>
      </div>
    </div>
  )
}

const LabelExpired = ({
  isRequesting,
  failed,
  onRequest
}: {
  isRequesting: boolean
  failed: boolean
  onRequest: () => void
}) => {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-4 p-4 lg:flex-col lg:py-5 lg:text-center">
      <LabelPrinter feed="printed">
        <LabelSheet className="w-full" blank />
      </LabelPrinter>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-3 lg:items-center lg:gap-4">
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-neutral-900">
            {t('tracking_new.label_expired_title')}
          </p>
          <p className="text-xs font-normal leading-relaxed text-neutral-600">
            {t('tracking_new.label_expired_body')}
          </p>
          {failed ? (
            <p role="alert" className="text-xs font-normal text-red-600">
              {t('tracking_new.label_new_failed')}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onRequest}
          disabled={isRequesting}
          className="rounded-md border border-neutral-300/70 bg-white px-3 py-1.5 text-xs font-medium text-neutral-900 shadow-sm transition-colors hover:bg-neutral-100 disabled:opacity-60"
        >
          {t(
            isRequesting
              ? 'tracking_new.label_new_requesting'
              : 'tracking_new.label_new_request'
          )}
        </button>
      </div>
    </div>
  )
}

// The label rests half out of the printer until the shopper asks for it.
type Feed = 'partial' | 'printing' | 'printed'

const LabelReady = ({
  labelURL,
  processId
}: {
  labelURL: string
  processId?: string
}) => {
  const { t } = useTranslation()
  const posthog = usePostHog()

  const [feed, setFeed] = useState<Feed>('partial')
  const printTimeout = useRef<number>()

  useEffect(() => () => window.clearTimeout(printTimeout.current), [])

  const print = (event: 'Download Label' | 'Print Label') => {
    if (feed === 'printing') return
    posthog?.capture(event, { processId })
    if (feed === 'printed') {
      openLabel(labelURL)
      return
    }
    setFeed('printing')
    printTimeout.current = window.setTimeout(() => {
      setFeed('printed')
      openLabel(labelURL)
    }, PRINT_ANIMATION_MS)
  }

  const isPrinting = feed === 'printing'

  return (
    <div className="flex items-start gap-4 p-4 lg:flex-col lg:items-center lg:py-5">
      <LabelPrinter feed={feed}>
        <LabelSheet className="w-full" />
      </LabelPrinter>
      <div className="flex min-w-0 flex-1 flex-col gap-3 lg:w-full lg:gap-4">
        <div className="space-y-1.5 lg:text-center">
          <p className="text-sm font-medium text-neutral-900">
            {t('tracking_new.label_ready_title')}
          </p>
          <p className="text-xs font-normal leading-relaxed text-neutral-600">
            {t('tracking_new.label_ready_hint')}
          </p>
        </div>
        <div className="flex flex-col gap-2 lg:flex-row">
          <button
            type="button"
            onClick={() => print('Download Label')}
            disabled={isPrinting}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-blue-500 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-600 disabled:opacity-80"
          >
            <DownloadIcon className="size-4 shrink-0" strokeWidth={1.5} />
            {t(
              isPrinting
                ? 'tracking_new.label_printing'
                : 'tracking_new.download_label_short'
            )}
          </button>
          <button
            type="button"
            onClick={() => print('Print Label')}
            disabled={isPrinting}
            className="hidden flex-1 items-center justify-center gap-2 rounded-md border border-neutral-300/70 bg-white px-3 py-2 text-sm font-medium text-neutral-900 shadow-sm transition-colors hover:bg-neutral-100 disabled:opacity-80 lg:flex"
          >
            <PrinterIcon className="size-4 shrink-0" strokeWidth={1.5} />
            {t('tracking_new.print_label')}
          </button>
        </div>
      </div>
    </div>
  )
}

// Safari drops the click's user activation during the print animation, so fall
// back to this tab. Not 'noopener': it makes window.open always return null.
const openLabel = (url: string) => {
  const tab = window.open(url, '_blank')
  if (tab) tab.opener = null
  else window.location.href = url
}

// The printer from the "Generating label" frame: the sheet slides down out of
// its slot, so whatever is still above the slot is hidden inside it.
const LabelPrinter = ({
  feed,
  children
}: {
  feed: Feed
  children: React.ReactNode
}) => {
  return (
    <div className="flex w-[124px] shrink-0 flex-col items-center lg:w-[196px]">
      <div className="relative z-10 h-4 w-full rounded-md bg-neutral-200 shadow-sm lg:h-5">
        <div className="absolute inset-x-2 bottom-1 h-0.5 rounded-full bg-neutral-400/70 lg:inset-x-3" />
      </div>
      <div className="relative -mt-1 h-[160px] w-[114px] overflow-hidden lg:h-[248px] lg:w-[180px]">
        <div
          className={`relative shadow-[0px_6px_6px_0px_rgba(0,0,0,0.04),0px_2px_4px_0px_rgba(0,0,0,0.08)] ${feedClassName[feed]}`}
        >
          {children}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-black/10 to-transparent" />
      </div>
    </div>
  )
}

const feedClassName: Record<Feed, string> = {
  partial: '-translate-y-[62%]',
  printing: 'animate-label-feed motion-reduce:animate-none',
  printed: 'translate-y-0'
}

export const QrLabel = ({
  labelURL,
  processId
}: {
  labelURL: string
  processId?: string
}) => {
  const { t } = useTranslation()
  const posthog = usePostHog()

  return (
    <div className="flex flex-col items-center gap-3 px-4 py-4">
      <div className="flex w-full items-center gap-3">
        <BarcodeIcon
          className="size-4 shrink-0 text-neutral-800"
          strokeWidth={1.5}
        />
        <span className="text-sm font-medium text-neutral-900">
          {t('tracking_new.show_qr_label')}
        </span>
      </div>
      <img
        src={labelURL}
        alt={t('tracking_new.show_qr_label')}
        className="size-44 rounded-md border border-neutral-300/50"
        onLoad={() => posthog?.capture('QR Label Shown', { processId })}
      />
      <p className="text-center text-xs font-normal leading-relaxed text-neutral-600">
        {t('tracking_new.show_qr_label_hint')}
      </p>
    </div>
  )
}
